import { queryDocuments } from "../firebase/firestore";

export const rankingService = {
    async buildRankingRow(user) {
        let totalCreditsEarned = 0;
        let totalCreditsConsumed = 0;
        let currentCreditBalance = Number(user?.totalCredits || 0);
        try {
            const credits = await queryDocuments("credits", [["assignedTo", "==", user.id]]);
            totalCreditsEarned = credits
                .filter((item) => Number(item.amount || 0) > 0)
                .reduce((sum, item) => sum + Number(item.amount || 0), 0);
            totalCreditsConsumed = Math.abs(
                credits
                    .filter((item) => Number(item.amount || 0) < 0)
                    .reduce((sum, item) => sum + Number(item.amount || 0), 0)
            );
            currentCreditBalance = totalCreditsEarned - totalCreditsConsumed;
        } catch (error) {
            totalCreditsEarned = Number(user?.totalCredits || 0);
            totalCreditsConsumed = 0;
            currentCreditBalance = Number(user?.totalCredits || 0);
        }
        return {
            id: user.id,
            userId: user.id,
            firstName: user?.firstName || "",
            lastName: user?.lastName || "",
            username: user?.username || user?.displayName || "",
            email: user?.email || "",
            phone: user?.phone || "",
            course: user?.course || "",
            academicYear: user?.academicYear || user?.year || "",
            collegeName: user?.collegeName || "",
            avatarUrl: user?.photoURL || user?.avatarUrl || "",
            totalCreditsEarned,
            totalCreditsConsumed,
            currentCreditBalance
        };
    },

    async getGlobalRanking(limit = 1000) {
        const allUsers = await queryDocuments("users", [["role", "==", "student"]]);
        const rankings = await Promise.all(
            allUsers.map((user) => this.buildRankingRow(user))
        );
        return rankings
            .sort((a, b) => {
                if (b.currentCreditBalance !== a.currentCreditBalance) {
                    return b.currentCreditBalance - a.currentCreditBalance;
                }
                if (b.totalCreditsEarned !== a.totalCreditsEarned) {
                    return b.totalCreditsEarned - a.totalCreditsEarned;
                }
                return a.firstName.localeCompare(b.firstName);
            })
            .slice(0, limit)
            .map((item, index) => ({
                ...item,
                rank: index + 1
            }));
    },

    async getRankingByCollege(collegeName, limit = 1000) {
        const collegeUsers = await queryDocuments("users", [
            ["role", "==", "student"],
            ["collegeName", "==", collegeName]
        ]);

        const rankings = await Promise.all(
            collegeUsers.map((user) => this.buildRankingRow(user))
        );

        return rankings
            .sort((a, b) => {
                if (b.currentCreditBalance !== a.currentCreditBalance) {
                    return b.currentCreditBalance - a.currentCreditBalance;
                }
                if (b.totalCreditsEarned !== a.totalCreditsEarned) {
                    return b.totalCreditsEarned - a.totalCreditsEarned;
                }
                return a.firstName.localeCompare(b.firstName);
            })
            .slice(0, limit)
            .map((item, index) => ({
                ...item,
                rank: index + 1
            }));
    },

    async getUserRank(userId) {
        const globalRanking = await this.getGlobalRanking(1000);
        const userRankData = globalRanking.find((r) => r.userId === userId);

        if (!userRankData) {
            return null;
        }

        return {
            ...userRankData,
            totalStudents: globalRanking.length,
            percentile: ((globalRanking.length - userRankData.rank) / globalRanking.length) * 100
        };
    },

    async getUserRankInCollege(userId, collegeName) {
        const collegeRanking = await this.getRankingByCollege(collegeName, 1000);
        const userRankData = collegeRanking.find((r) => r.userId === userId);

        if (!userRankData) {
            return null;
        }

        return {
            ...userRankData,
            totalInCollege: collegeRanking.length,
            percentile: ((collegeRanking.length - userRankData.rank) / collegeRanking.length) * 100
        };
    },

    async getStartupRanking(limit = 50) {
        const allStartups = await queryDocuments("startups", [["status", "==", "active"]]);

        const startupRankings = allStartups.map((startup, index) => ({
            id: startup.id,
            name: startup.name,
            foundedBy: startup.foundedBy,
            stage: startup.stage,
            score: startup.score || 0,
            rank: index + 1
        }));

        return startupRankings.sort((a, b) => b.score - a.score);
    }
};

export default rankingService;
